// Common export utility for orders and quotes
import Utils from './index';

/**
 * Export data as file (CSV, Excel, PDF) from API endpoint
 * @param {Object} options
 * @param {string} options.endpoint - API endpoint (e.g. '/orders/', '/quotes/')
 * @param {Object} options.params - Query params for export
 * @param {string} options.format - 'csv' | 'excel' | 'pdf'
 * @param {string} options.filenameBase - Base filename (e.g. 'orders', 'quotes')
 * @param {function} options.setLoading - Function to set loading state
 * @param {function} [options.onSuccess] - Optional callback on success
 * @param {function} [options.onError] - Optional callback on error
 */
export function exportFile({ endpoint, params, format, filenameBase, setLoading, onSuccess, onError }) {
  setLoading(true);
  
  // Extract order_ids and quote_ids from params (they go in payload, not query string)
  const orderIds = params.order_ids || [];
  const quoteIds = params.quote_ids || [];
  
  // Create params copy without order_ids, quote_ids, and payment_method
  const queryParams = { ...params };
  delete queryParams.order_ids;
  delete queryParams.quote_ids;
  
  const buildQueryString = (obj) => {
    const pairs = [];
    Object.keys(obj || {}).forEach(key => {
      if (Array.isArray(obj[key])) {
        pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(obj[key].join(','))}`);
      } else {
        pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`);
      }
    });
    return pairs.length ? '?' + pairs.join('&') : '';
  };

  const queryString = buildQueryString(queryParams);
  const isBinary = format === 'excel' || format === 'pdf';
  
  // Build payload with order_ids and payment_method
  const payload = {
    "export_format": format,
    ...(orderIds.length > 0 && { "order_ids": orderIds }),
    ...(quoteIds.length > 0 && { "quote_ids": quoteIds }),
  };
  
  Utils.api.postApiCall(
    endpoint + queryString,
    payload,
    (resp) => {
      let filename = `${filenameBase}.${format === 'excel' ? 'xlsx' : format}`;
      if (isBinary) {
        const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (typeof resp.data === 'string') {
        const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (resp?.data?.file_url) {
        window.open(resp.data.file_url, '_blank');
      } else {
        alert('Export failed: No file returned');
      }
      setLoading(false);
      if (onSuccess) onSuccess();
    },
    (err) => {
      setLoading(false);
      const errorMessage = typeof err === 'string' ? err : (err?.message || err?.error || 'Too many records. Please filter and export up to 10,000 at a time.');
      Utils.showAlert(2, errorMessage);
      if (onError) onError(err);
    },
    isBinary ? { responseType: 'blob' } : {}
  );
}
